import React, { useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { IconButton, CloseIcon } from './Button';
import useModalFocus from './useModalFocus';
import './ui.css';

/**
 * Side panel (the admin user drawer pattern): modal, with a scrim, a focus
 * trap, Escape and a close button. Rendered into <body> under its own `ui-v2`
 * scope, and marked `data-pane-overlay` so an open unit pane does not treat a
 * click inside it as an outside click.
 */
export function Drawer({ open, onClose, title, subtitle, children, footer, width }) {
    const titleId = `${useId().replace(/:/g, '')}-drawer-title`;
    const panelRef = useRef(null);
    const layerRef = useRef(null);
    const onKeyDown = useModalFocus(panelRef, open, { onEscape: onClose, layerRef });

    if (!open) return null;
    return createPortal(
        <div className="ui-v2 v2-layer" data-pane-overlay="true" ref={layerRef}>
            {/* preventDefault: a mousedown on the scrim would otherwise move focus
                to <body> after the close, undoing the focus return (QA B5). */}
            <div className="v2-scrim" onMouseDown={(e) => { e.preventDefault(); onClose(); }} />
            <aside
                ref={panelRef}
                className="v2-drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onKeyDown={onKeyDown}
                // 100% of the viewport box, not 100vw: 100vw includes a classic scrollbar (QA A-7).
                style={width ? { width: `min(${width}px, 100%)` } : undefined}
            >
                <div className="v2-dr-head">
                    <div className="v2-dr-titles">
                        <h2 id={titleId}>{title}</h2>
                        {subtitle && <p className="v2-dr-sub">{subtitle}</p>}
                    </div>
                    <IconButton label="Close" onClick={onClose}><CloseIcon /></IconButton>
                </div>
                <div className="v2-dr-body">{children}</div>
                {footer && <div className="v2-dr-foot">{footer}</div>}
            </aside>
        </div>,
        document.body,
    );
}

Drawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.node.isRequired,
    subtitle: PropTypes.node,
    children: PropTypes.node,
    footer: PropTypes.node,
    width: PropTypes.number,
};
